import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the steps for the checkout process.
export type CheckoutStep = "billing" | "shipping" | "payment";

/**
 * Defines the shape of the checkout state managed by Redux.
 */
export interface CheckoutState {
  /** The currently active step in the checkout flow. */
  currentStep: CheckoutStep;

  /** The ID of the chosen payment gateway (e.g., 'bacs', 'cod', 'stripe'). */
  selectedPaymentMethod: string | null;

  /** A flag to indicate when the final 'processCheckout' mutation is in flight. */
  isProcessing: boolean;

  /** Stores server-side validation errors returned from the server. */
  serverErrors: Record<string, string> | null;

  /**
   * Determines if the shipping address should be the same as the billing address.
   */
  useBillingForShipping: boolean;

  /**
   * The customer note/order comments.
   */
  customerNote: string;

  /**
   * Tracks the state of the newsletter/mailing list subscription checkbox.
   */
  isMailingSubscribed: boolean;

  // --- PLUXEE STATE ---
  pluxeeGsm: string;
  pluxeeOtp: string;
}

// Initialize currentStep from URL if present, otherwise default to 'billing'
const getInitialStep = (): CheckoutStep => {
  const params = new URLSearchParams(window.location.search);
  const step = params.get("step");
  const validSteps: CheckoutStep[] = ["billing", "shipping", "payment"];
  return validSteps.includes(step as CheckoutStep)
    ? (step as CheckoutStep)
    : "billing";
};

const initialState: CheckoutState = {
  currentStep: getInitialStep(),
  selectedPaymentMethod: null,
  isProcessing: false,
  serverErrors: null,
  useBillingForShipping: true,
  customerNote: "",
  isMailingSubscribed: false,
  pluxeeGsm: "",
  pluxeeOtp: "",
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.currentStep = action.payload;
    },
    setSelectedPaymentMethod: (state, action: PayloadAction<string | null>) => {
      state.selectedPaymentMethod = action.payload;
    },
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    setServerErrors: (
      state,
      action: PayloadAction<Record<string, string> | null>,
    ) => {
      state.serverErrors = action.payload;
    },
    setUseBillingForShipping: (state, action: PayloadAction<boolean>) => {
      state.useBillingForShipping = action.payload;
    },
    setCustomerNote: (state, action: PayloadAction<string>) => {
      state.customerNote = action.payload;
    },
    setIsMailingSubscribed: (state, action: PayloadAction<boolean>) => {
      state.isMailingSubscribed = action.payload;
    },
    // --- PLUXEE REDUCERS ---
    setPluxeeGsm: (state, action: PayloadAction<string>) => {
      state.pluxeeGsm = action.payload;
    },
    setPluxeeOtp: (state, action: PayloadAction<string>) => {
      state.pluxeeOtp = action.payload;
    },
    resetCheckout: () => initialState,
  },
});

export const {
  setCurrentStep,
  setSelectedPaymentMethod,
  setIsProcessing,
  setServerErrors,
  setUseBillingForShipping,
  setCustomerNote,
  setIsMailingSubscribed,
  setPluxeeGsm,
  setPluxeeOtp,
  resetCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
